import React from 'react';
import { Alert, Box, CircularProgress, Divider, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { CUSTOMER_COLORS } from '../../constants/customerTheme';
import { formatCurrency } from '../../utils/currencyUtils';

const BLOCKED_REASON_KEYS = {
  TooLate: 'customer.appointments.myAppointments.refund.blockedTooLate',
  AlreadyStarted: 'customer.appointments.myAppointments.refund.blockedStarted',
  AlreadyCancelled: 'customer.appointments.myAppointments.refund.blockedCancelled',
  Completed: 'customer.appointments.myAppointments.refund.blockedCompleted',
};

const RETAINED_REASON_KEYS = {
  Deposit: 'customer.appointments.myAppointments.refund.retainedDeposit',
  ProcessingFee: 'customer.appointments.myAppointments.refund.retainedFee',
  LateCancellation: 'customer.appointments.myAppointments.refund.retainedLate',
};

const Row = ({ label, value, strong = false, color }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
    <Typography variant="body2" color={strong ? undefined : 'text.secondary'} sx={{ fontWeight: strong ? 700 : 400 }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: strong ? 700 : 500, whiteSpace: 'nowrap', color }}>
      {value}
    </Typography>
  </Box>
);

/**
 * Tells the customer, before they confirm, exactly what they get back. A partial refund they
 * discover on their statement is how a cancellation turns into a card dispute.
 */
const AppointmentCancellationDetails = ({ preview, loading, failed, currencyCode }) => {
  const { t } = useTranslation();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
        <CircularProgress size={18} sx={{ color: CUSTOMER_COLORS.primary }} />
        <Typography variant="body2" color="text.secondary">{t('customer.appointments.myAppointments.refund.loading')}</Typography>
      </Box>
    );
  }

  if (failed) {
    return <Alert severity="warning" sx={{ mt: 2 }}>{t('customer.appointments.myAppointments.refund.error')}</Alert>;
  }

  if (!preview) {
    return null;
  }

  if (!preview.canCancel) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {t(BLOCKED_REASON_KEYS[preview.reasonCode] || 'customer.appointments.myAppointments.refund.blockedGeneric', {
          hours: preview.cancellationWindowHours,
        })}
      </Alert>
    );
  }

  if (preview.amountPaid <= 0) {
    return preview.isLateCancellation ? (
      <Alert severity="info" sx={{ mt: 2 }}>
        {t('customer.appointments.myAppointments.refund.lateNothingPaid', { hours: preview.cancellationWindowHours })}
      </Alert>
    ) : null;
  }

  const currency = preview.currency || currencyCode;
  const money = (amount) => formatCurrency(amount, currency);

  return (
    <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
      {preview.isLateCancellation ? (
        <Alert severity="error">
          {t('customer.appointments.myAppointments.refund.lateWarning', { hours: preview.cancellationWindowHours })}
        </Alert>
      ) : null}
      <Row label={t('customer.appointments.myAppointments.refund.paid')} value={money(preview.amountPaid)} />
      {preview.nonRefundableAmount > 0 ? (
        <Row label={t('customer.appointments.myAppointments.refund.kept')} value={`- ${money(preview.nonRefundableAmount)}`} />
      ) : null}
      <Divider />
      <Row
        label={t('customer.appointments.myAppointments.refund.youGetBack')}
        value={money(preview.refundAmount)}
        strong
        color={CUSTOMER_COLORS.primary}
      />
      {preview.nonRefundableAmount > 0 && RETAINED_REASON_KEYS[preview.nonRefundableBasis] ? (
        <Typography variant="caption" color="text.secondary">
          {t(RETAINED_REASON_KEYS[preview.nonRefundableBasis])}
        </Typography>
      ) : null}
      {preview.refundAmount > 0 ? (
        <Typography variant="caption" color="text.secondary">{t('customer.appointments.myAppointments.refund.timing')}</Typography>
      ) : null}
    </Box>
  );
};

export default AppointmentCancellationDetails;
